import React, { useEffect, useRef, useState } from 'react'
import { PRE_CHECK_MACHINE_STATES } from '../constants'
import useStudentPreChecks from '../useStudentPreChecks'
import useWindowEvents from '../useWindowEvents'
import StudentVideo from '../../Student/StudentVideo'
import { prepareMicForRecord } from '../../audiocapture/recorder'
import { getUserData } from '../../../services/API'
import { COPY_STRINGS } from '../../../constants/translatedCopy'
import { READY_MODULE_VIDEO_MAX_DURATION_SECONDS } from '../../../constants/readyModuleVideos'
import getIntroVideoURL from './getIntroVideoURL'
import './IntroVideo.scss'

export const INTRO_VIDEO_EVENTS = {
    STARTED: 'INTRO_VIDEO_STARTED',
    PLAYBACK_STARTED: 'INTRO_VIDEO_PLAYBACK_STARTED',
    COMPLETED: 'INTRO_VIDEO_COMPLETED',
    SKIPPED: 'INTRO_VIDEO_SKIPPED',
    ERRORED: 'INTRO_VIDEO_ERRORED',
    NEVER_STARTED: 'INTRO_VIDEO_NEVER_STARTED',
    NOT_CONFIGURED: 'INTRO_VIDEO_NOT_CONFIGURED',
    SKIPPED_TUTOR_SEEN_FTUE: 'INTRO_VIDEO_SKIPPED_TUTOR_SEEN_FTUE',
}

/**
 * How long to wait for playback to start before giving up and handing off to the checks.
 * Named for what it measures: the play event, which fires when play() is called, not when
 * a frame is painted.
 *
 * StudentVideo's own fallbacks only arm once playback has begun, so nothing covers a video
 * that never starts at all — a link too slow to buffer the opening data, or autoplay blocked
 * with nobody tapping the play button. Generous enough that a slow-but-working connection
 * still gets the video: playback needs roughly the 55 KB moov plus a few hundred KB of
 * media, which is around 25s even on a badly degraded link.
 *
 * A student being prompted to tap the play button is waiting on a human rather than a
 * network, and this clock does not know the difference. See TT-935 follow-up.
 */
const PLAYBACK_START_TIMEOUT_MS = 60000

/**
 * Set on the body for as long as the video is on screen.
 *
 * The two things the video step has to get out of its own way — the avatar rig and the
 * paper frame — are both rendered outside the Ready Module's subtree, so no class on the
 * step itself can reach them. See IntroVideo.scss for what the class hides and why.
 */
export const INTRO_VIDEO_BODY_CLASS = 'ready-module-intro-video-active'

/**
 * TT-935: plays the "how to work with Amira" video at the top of the Ready Module,
 * before any of the checks run.
 *
 * Assess plays it every session. Tutor plays it only for a student who has not been
 * through the first time experience yet, which is what settings.hasSeenFTUE reports.
 * See chooseIntroVideo below.
 *
 * The video is never allowed to block a student: a missing URL, a playback error, or the
 * skip button all finish the step and hand off to the checks.
 */
export default function IntroVideo(props) {

    const { speechProps } = props

    const studentPreCheck = useStudentPreChecks({ itemToCheck: PRE_CHECK_MACHINE_STATES.introVideo })
    const { isCurrent, settings } = studentPreCheck

    const [videoURL, setVideoURL] = useState(null)
    const playbackStartTimer = useRef(null)

    useWindowEvents()

    useEffect(() => {
        if (isCurrent) {
            studentPreCheck.startCheck()
            resolveIntroVideo()
        }
    }, [isCurrent])

    useEffect(() => () => {
        clearPlaybackStartTimer()
        // Belt and braces against the one failure this class can cause. The effect below
        // already removes it, but a class left on the body outlives the component, and the
        // symptom — no Amira and a blank paper frame for the rest of the session — gives a
        // student nothing to recover from. classList.remove is idempotent, so removing it
        // twice on the ordinary path costs nothing.
        document.body.classList.remove(INTRO_VIDEO_BODY_CLASS)
    }, [])

    // Deliberately keyed on exactly what the render below checks. Keying it on videoURL
    // alone would leave the class behind once the step stops being current, because the
    // URL survives a render that returns null — and every check after this one would run
    // with the avatar hidden and the frame blank.
    const isShowingVideo = isCurrent && !!videoURL

    useEffect(() => {
        if (!isShowingVideo) return undefined
        document.body.classList.add(INTRO_VIDEO_BODY_CLASS)
        return () => document.body.classList.remove(INTRO_VIDEO_BODY_CLASS)
    }, [isShowingVideo])

    const clearPlaybackStartTimer = () => {
        if (playbackStartTimer.current) {
            clearTimeout(playbackStartTimer.current)
            playbackStartTimer.current = null
        }
    }

    /**
     * Decides whether this session gets the video, and with which asset.
     * Finishes the step immediately when it does not.
     */
    const resolveIntroVideo = () => {
        try {
            chooseIntroVideo()
        } catch (error) {
            // Deciding which video to play must never strand a student: without this the
            // throw goes unhandled, finishCheck never runs, and the Ready Module stops
            // dead on a blank screen.
            studentPreCheck.logEvent(INTRO_VIDEO_EVENTS.ERRORED, PRE_CHECK_MACHINE_STATES.introVideo, {
                error: (error && error.message) || String(error),
            })
            studentPreCheck.finishCheck()
        }
    }

    const chooseIntroVideo = () => {
        const url = getIntroVideoURL({ forcedAssessment: settings.forcedAssessment })

        if (!url) {
            // Safety net: every audience and locale resolves today, but a future gap in
            // the asset table must not strand a student on a blank screen.
            studentPreCheck.logEvent(INTRO_VIDEO_EVENTS.NOT_CONFIGURED)
            studentPreCheck.finishCheck()
            return
        }

        // TT-1215: the tutor gate is the student's hasSeenFTUE, deliberately not the
        // microphone permission this used to read. Permission is scoped to the origin,
        // browser and device, so it says nothing about who is sitting there — on a shared
        // classroom device, or any machine an adult had already set up, the first student
        // to reach tutor was read as a returning one and never got the video.
        // hasSeenFTUE follows the student instead: the Ready Module sets it once the
        // checks finish, and App sets it for anyone with a prior completed activity, so
        // it already means "this student has worked with Amira before".
        const gateOnReturningStudent = !settings.forcedAssessment && !settings.readyModuleTestMode

        if (gateOnReturningStudent && settings.hasSeenFTUE) {
            studentPreCheck.logEvent(INTRO_VIDEO_EVENTS.SKIPPED_TUTOR_SEEN_FTUE)
            studentPreCheck.finishCheck()
            return
        }

        // Log what drove the choice — which video a session gets is the easiest thing
        // to get wrong here, and the hardest to diagnose after the fact.
        const userData = getUserData() || {}
        studentPreCheck.logEvent(INTRO_VIDEO_EVENTS.STARTED, PRE_CHECK_MACHINE_STATES.introVideo, {
            audience: settings.forcedAssessment ? 'assess' : 'tutor',
            forcedAssessment: !!settings.forcedAssessment,
            grade: userData.actualGrade,
            locale: userData.locale,
            videoURL: url,
        })
        setVideoURL(url)
        playbackStartTimer.current = setTimeout(() => {
            playbackStartTimer.current = null
            // Usually a no-op: StudentVideo only takes the mic in playVideo, which runs off
            // loadeddata, so a video that never got a first frame never took it. It matters
            // for the paths that did take it and cannot give it back — playVideo grabs the
            // mic before it checks for the player element and returns without restoring if
            // that check fails, and there is a gap between it taking the mic and the play
            // event that disarms this timer. Neither runs onEnd, so nothing else restores.
            handMicBackToRecorder()
            finishWith(INTRO_VIDEO_EVENTS.NEVER_STARTED)
        }, PLAYBACK_START_TIMEOUT_MS)
    }

    const finishWith = (event) => {
        clearPlaybackStartTimer()
        studentPreCheck.logEvent(event)
        studentPreCheck.finishCheck()
    }

    /**
     * The media loaded far enough to be playable and play() was called. Not the same as
     * frames being on screen — the play event fires on the call, not on the first painted
     * frame — but it is the point where the asset is known to have arrived, which is what
     * distinguishes this from STARTED. STARTED only says a URL was chosen, so a stall
     * between the two shows up in the logs purely as a missing terminal event.
     */
    const onPlaybackStarted = ({ duration }) => {
        clearPlaybackStartTimer()
        studentPreCheck.logEvent(INTRO_VIDEO_EVENTS.PLAYBACK_STARTED, PRE_CHECK_MACHINE_STATES.introVideo, {
            duration,
        })
    }

    /**
     * StudentVideo hands the microphone back to the recorder in its own onEnd, but any path
     * that unmounts the player before that runs leaves the mic parked in playback mode with
     * the recorder stream cancelled, which would break the audio checks that follow.
     */
    const handMicBackToRecorder = () => {
        prepareMicForRecord()
        if (typeof speechProps?.restartRecorderStreamIfNeeded === 'function') {
            speechProps.restartRecorderStreamIfNeeded()
        }
    }

    const skipVideo = () => {
        handMicBackToRecorder()
        finishWith(INTRO_VIDEO_EVENTS.SKIPPED)
    }

    if (!isShowingVideo) return null

    return <div className='ready-module-intro-video'>
        <div className='ready-module-intro-video__player'>
            <StudentVideo
                videoURL={videoURL}
                speech={speechProps}
                onDone={() => finishWith(INTRO_VIDEO_EVENTS.COMPLETED)}
                onError={() => finishWith(INTRO_VIDEO_EVENTS.ERRORED)}
                onPlaybackStarted={onPlaybackStarted}
                maxDurationSeconds={READY_MODULE_VIDEO_MAX_DURATION_SECONDS}
                unmountWhenDone={true}
                logToSentry={true}
            />
        </div>
        <button
            className='ready-module-intro-video__skip'
            aria-label={COPY_STRINGS.skip_intro_video}
            onClick={skipVideo}>
            {COPY_STRINGS.skip_intro_video}
        </button>
    </div>
}
